import { isEqual } from 'lodash';

import {
  BAG_KEYS,
  BAG_LVN_KEYS,
  BRANCH_TYPE,
  BRANCH_TYPE_TO_VARIABLE_NAME,
  COUNTRY_OPTIONS,
  EINTRAGUNG_ALS_ID,
  EIV_ENTRY_TYPE,
  EIV_ENTRY_TYPE_TO_GERMAN_NAME,
  EIV_STATUS,
  EIV_STATUS_TO_SHOW,
  EIV_STATUS_TO_STRING,
  INSTALLER_ENTRIES,
  KEYS_TO_GERMAN,
  TENANT_ID,
  TENANT_WITH_BIRTHDATE,
} from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';

import {
  conditionalChangeEntry,
  conditionalStaticEntry,
  getEintragungAlsIdEntries,
  prepValueForEmail,
  prepValueForInitialData,
} from 'utils/DataUtils';
import { parseEIVDateStringToEfixDateString } from 'utils/DatesUtils';
import { prepDocumentForMutation } from 'utils/FileUtil';
import {
  areExpertContactsEnabled,
  getExpertStatusEnergy,
  getExpertStatusGas,
} from 'utils/FormUtils';
import {
  doesRegisteredInstallerHaveBranch,
  isRegisteredBranchGuest,
  isRegisteredInstallerOnlyHilfsbetrieb,
} from 'utils/InstallerUtils';
import { QUALIFICATION_ID_FIELDS, mapExpertToFachkraftPayload } from 'utils/qualificationBasisData';
import { getTenantId } from 'utils/tenantUtil';

import { getFieldByName } from './DebFormSectionUtil';
import { shouldFillDebTestData } from './DebTestSetup';

const mapCountryNameToCode = (name) => {
  if (!name) return 'DE';
  const found = COUNTRY_OPTIONS.find((opt) => opt.title === name || opt.value === name);
  return found ? found.value : 'DE';
};

const mapCountryCodeToName = (code) => {
  if (!code) return code;
  const found = COUNTRY_OPTIONS.find((opt) => opt.value === code);
  return found ? found.title : code;
};

const prepDebSeals = (initialData, firstPageValues) => {
  const sealsEntries = firstPageValues.seals.map((seal) => {
    const oldSeal = initialData.seals.find((searchSeal) => searchSeal.id === seal.id);
    const lostDate = seal.lostDate || oldSeal.lostOn;
    let sealAction = '';
    if (isEqual(oldSeal, seal)) {
      sealAction = 'Unverändert';
    } else if (seal.lostDate !== oldSeal.lostDate) {
      sealAction = 'Verloren';
    }
    return [
      {
        first: 'Änderung',
        second: {
          first: sealAction,
          second: 'Unverändert',
        },
      },
      {
        first: 'Id',
        second: {
          first: String(seal.id),
          second: String(oldSeal.id),
        },
      },
      {
        first: 'Zangen/Nuss Nr.',
        second: {
          first: seal.sealNumber,
          second: oldSeal.sealNumber,
        },
      },
      conditionalChangeEntry(
        lostDate,
        'Verlustdatum',
        prepValueForEmail(lostDate),
        prepValueForEmail(oldSeal.lostOn)
      ),
      conditionalChangeEntry(
        seal.lostReason || oldSeal.lostReason,
        'Grund des Verlustes',
        prepValueForEmail(seal.lostReason),
        prepValueForEmail(oldSeal.lostReason)
      ),
    ].filter(Boolean);
  });
  return sealsEntries;
};

const isExpertBranchExtendedFromCheckbox = (
  expert,
  branch,
  installerStatusSummary,
  branchCheckboxValue
) => {
  if (
    !isRegisteredBranchGuest(installerStatusSummary, branch) &&
    EIV_STATUS_TO_SHOW.includes(expert[`status${BRANCH_TYPE_TO_VARIABLE_NAME[branch]}`])
  ) {
    return branchCheckboxValue;
  }
  return false;
};

const getExpertActions = (expert, oldExpert, firstPageValues, installerStatusSummary) => {
  let energyAction = 'Unverändert';
  let gasAction = 'Unverändert';

  if (!oldExpert) {
    if (expert.statusGas === EIV_STATUS.NEW) {
      gasAction = 'Neu';
    }
    if (expert.statusEnergy === EIV_STATUS.NEW) {
      energyAction = 'Neu';
    }
    return {
      energyAction,
      gasAction,
    };
  }

  const isEnergyBranchExtendedFromCheckbox = isExpertBranchExtendedFromCheckbox(
    expert,
    BRANCH_TYPE.ENERGY,
    installerStatusSummary,
    firstPageValues.energyExtension
  );
  const isGasBranchExtendedFromCheckbox = isExpertBranchExtendedFromCheckbox(
    expert,
    BRANCH_TYPE.GAS,
    installerStatusSummary,
    firstPageValues.gasExtension
  );
  const isEnergyBranchExtendedFromButton =
    expert.isExpertExtendedEnergy &&
    expert.isExpertExtendedEnergy !== oldExpert?.isExpertExtendedEnergy;
  const isGasBranchExtendedFromButton =
    expert.isExpertExtendedGas && expert.isExpertExtendedGas !== oldExpert?.isExpertExtendedGas;
  if (
    !isEnergyBranchExtendedFromCheckbox &&
    !isGasBranchExtendedFromCheckbox &&
    isEqual(expert, oldExpert)
  ) {
    energyAction = 'Unverändert';
    gasAction = 'Unverändert';
  }

  // EXTENDED ACTION FOR GAS AND BOTH

  if (EIV_STATUS_TO_SHOW.includes(expert.statusGas)) {
    if (isGasBranchExtendedFromCheckbox || isGasBranchExtendedFromButton) {
      gasAction = 'Verlängert';
    }
    if (
      expert.expertGasValidityDate &&
      expert.expertGasValidityDate !== oldExpert?.expertGasValidityDate
    ) {
      gasAction = 'Verlängert';
    }
  }
  // EXTENDED ACTION FOR ENERGY

  if (EIV_STATUS_TO_SHOW.includes(expert.statusEnergy)) {
    if (isEnergyBranchExtendedFromCheckbox || isEnergyBranchExtendedFromButton) {
      energyAction = 'Verlängert';
    }
    if (
      expert.expertEnergyValidityDate &&
      expert.expertEnergyValidityDate !== oldExpert?.expertEnergyValidityDate
    ) {
      energyAction = 'Verlängert';
    }
  }

  // DELETED ACTION
  if (expert.statusGas === EIV_STATUS.DELETED) {
    gasAction = 'Gelöscht';
  }
  if (expert.statusEnergy === EIV_STATUS.DELETED) {
    energyAction = 'Gelöscht';
  }

  return {
    energyAction,
    gasAction,
  };
};

const prepDebExperts = (
  initialData,
  firstPageValues,
  installerStatusSummary,
  qualificationBasisData
) => {
  const experts = [...firstPageValues.experts];
  const expertEntries = [];
  const hasEnergyRow = doesRegisteredInstallerHaveBranch(
    BRANCH_TYPE.ENERGY,
    installerStatusSummary
  );
  const hasGasRow = doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary);

  const isExpertValid = (expert) =>
    expert &&
    (expert.isNewExpert ||
      (hasGasRow && EIV_STATUS_TO_SHOW.includes(expert.statusGas)) ||
      (hasEnergyRow && EIV_STATUS_TO_SHOW.includes(expert.statusEnergy)));

  experts.forEach((expert) => {
    if (isExpertValid(expert)) {
      const oldExpert = expert.isNewExpert
        ? undefined
        : initialData.experts.find((search) => search.id === expert.id);
      const userActions = getExpertActions(
        expert,
        oldExpert,
        firstPageValues,
        installerStatusSummary
      );
      const shouldTryShowingValidityDate = ['Verlängert', 'Neu'];

      const doesExpertHaveEnergyBranch = doesRegisteredInstallerHaveBranch(
        BRANCH_TYPE.ENERGY,
        installerStatusSummary
      );
      const doesExpertHaveGasBranch = doesRegisteredInstallerHaveBranch(
        BRANCH_TYPE.GAS,
        installerStatusSummary
      );

      const isEnergyDeleted = expert.statusEnergy === EIV_STATUS.DELETED;
      const isGasDeleted = expert.statusGas === EIV_STATUS.DELETED;

      const formatExpertStatus = (status) => EIV_STATUS_TO_STRING[status ?? null];
      const fachkraft = mapExpertToFachkraftPayload(expert, qualificationBasisData);
      const oldFachkraft = oldExpert
        ? mapExpertToFachkraftPayload(oldExpert, qualificationBasisData)
        : undefined;
      expertEntries.push(
        [
          conditionalChangeEntry(
            doesExpertHaveEnergyBranch,
            'Änderung Strom',
            userActions.energyAction,
            'Unverändert'
          ),
          conditionalChangeEntry(
            doesExpertHaveGasBranch,
            'Änderung Gas',
            userActions.gasAction,
            'Unverändert'
          ),
          conditionalChangeEntry(
            Boolean(oldExpert?.id),
            'Id',
            String(oldExpert?.id),
            String(oldExpert?.id)
          ),
          {
            first: 'Vorname',
            second: { first: expert.firstName, second: prepValueForEmail(oldExpert?.firstName) },
          },
          {
            first: 'Nachname',
            second: { first: expert.lastName, second: prepValueForEmail(oldExpert?.lastName) },
          },
          ...QUALIFICATION_ID_FIELDS.map(([field, payloadField]) => ({
            first: field,
            second: {
              first: String(fachkraft[payloadField]),
              second: String(oldFachkraft?.[payloadField] ?? 0),
            },
          })),
          conditionalChangeEntry(
            doesExpertHaveEnergyBranch || doesExpertHaveGasBranch,
            'HWK Betriebsleiter',
            prepValueForEmail(expert.hwkBetriebsleiter),
            prepValueForEmail(oldExpert?.hwkBetriebsleiter)
          ),
          conditionalChangeEntry(
            doesExpertHaveEnergyBranch || doesExpertHaveGasBranch,
            'Inhaber',
            prepValueForEmail(expert.isExpertOwner),
            prepValueForEmail(oldExpert?.isExpertOwner)
          ),
          conditionalChangeEntry(
            areExpertContactsEnabled && expert.netzportalEmail,
            'E-Mail-Adresse',
            expert.netzportalEmail,
            prepValueForEmail(oldExpert?.netzportalEmail)
          ),
          conditionalChangeEntry(
            areExpertContactsEnabled && expert.telefon,
            'Telefonnummer',
            expert.telefon,
            prepValueForEmail(oldExpert?.telefon)
          ),
          conditionalChangeEntry(
            prepValueForEmail(expert.expertEnergyValidityDate) !== '-' ||
              prepValueForEmail(oldExpert?.expertEnergyValidityDate) !== '-',
            'Stromausweis Gültigkeitsdatum',
            prepValueForEmail(expert.expertEnergyValidityDate),
            prepValueForEmail(oldExpert?.expertEnergyValidityDate)
          ),
          conditionalChangeEntry(
            prepValueForEmail(expert.expertGasValidityDate) !== '-' ||
              prepValueForEmail(oldExpert?.expertGasValidityDate) !== '-',
            'Gasausweis Gültigkeitsdatum',
            prepValueForEmail(expert.expertGasValidityDate),
            prepValueForEmail(oldExpert?.expertGasValidityDate)
          ),
          conditionalChangeEntry(
            TENANT_WITH_BIRTHDATE.includes(getTenantId()) || Boolean(oldExpert?.birthdate),
            'Geburtsdatum',
            prepValueForEmail(expert.birthdate),
            prepValueForEmail(oldExpert?.birthdate)
          ),
          conditionalChangeEntry(
            isEnergyDeleted,
            'Grund der Löschung Strom',
            prepValueForEmail(expert.deletionReasonEnergy),
            '-'
          ),
          conditionalChangeEntry(
            isEnergyDeleted,
            'Löschdatum Strom',
            prepValueForEmail(expert.deletionDateEnergy),
            '-'
          ),
          conditionalChangeEntry(
            isGasDeleted,
            'Grund der Löschung Gas',
            prepValueForEmail(expert.deletionReasonGas),
            '-'
          ),
          conditionalChangeEntry(
            isGasDeleted,
            'Löschdatum Gas',
            prepValueForEmail(expert.deletionDateGas),
            '-'
          ),
          {
            first: 'statusStrom',
            second: {
              first: formatExpertStatus(expert.statusEnergy),
              second: formatExpertStatus(oldExpert?.statusEnergy),
            },
          },
          {
            first: 'statusGas',
            second: {
              first: formatExpertStatus(expert.statusGas),
              second: formatExpertStatus(oldExpert?.statusGas),
            },
          },
        ].filter(Boolean)
      );
    }
  });
  return expertEntries;
};

export const prepDebDataForBackend = (
  initialData,
  firstPageValues,
  secondPageValues,
  subject,
  documents,
  installerStatusSummary,
  comboBoxOptions,
  qualificationBasisData
) => {
  const formDataEntries = [];
  const expertEntries = prepDebExperts(
    initialData,
    firstPageValues,
    installerStatusSummary,
    qualificationBasisData
  );
  const shouldIncludeField = (key) => {
    if (BAG_KEYS.includes(key) && getTenantId() === TENANT_ID.BAG) {
      return true;
    }
    if (
      BAG_LVN_KEYS.includes(key) &&
      ([TENANT_ID.BAG, TENANT_ID.LVN].includes(getTenantId()) || Boolean(initialData[key]))
    ) {
      return true;
    }
    if (!BAG_KEYS.includes(key) && !BAG_LVN_KEYS.includes(key)) {
      return true;
    }
    return false;
  };
  Object.keys(KEYS_TO_GERMAN).forEach((key) => {
    const field = getFieldByName(key);
    if (!field) return;
    const hasExistingBAGLVNValue = BAG_LVN_KEYS.includes(key) && Boolean(initialData[key]);
    if (
      hasExistingBAGLVNValue ||
      evaluateCondition(field.whenShouldFieldRender, {
        isOnlyHilfsbetrieb: isRegisteredInstallerOnlyHilfsbetrieb(installerStatusSummary),
        installerStatusSummary,
      })
    ) {
      const resolveValue = (val) => (key === 'companyCountry' ? mapCountryCodeToName(val) : val);
      const entry = {
        first: KEYS_TO_GERMAN[key],
        second: {
          first: prepValueForEmail(resolveValue(firstPageValues[key]), key, comboBoxOptions),
          second: prepValueForEmail(resolveValue(initialData[key]), key, comboBoxOptions),
        },
      };

      if (shouldIncludeField(key)) {
        formDataEntries.push(entry);
      }
    }
  });

  const upsertFormDataEntry = (key) => {
    const fieldLabel = KEYS_TO_GERMAN[key];
    if (!fieldLabel) return;

    const hasFormValue = Object.prototype.hasOwnProperty.call(firstPageValues, key);

    const currentValue = hasFormValue
      ? prepValueForInitialData(firstPageValues[key])
      : (initialData[key] ?? null);

    const previousValue = initialData[key] ?? null;

    const nextEntry = {
      first: fieldLabel,
      second: {
        first: prepValueForEmail(currentValue, key, comboBoxOptions),
        second: prepValueForEmail(previousValue, key, comboBoxOptions),
      },
    };

    const existingIndex = formDataEntries.findIndex((entry) => entry.first === fieldLabel);

    if (existingIndex >= 0) {
      formDataEntries[existingIndex] = nextEntry;
    } else {
      formDataEntries.push(nextEntry);
    }
  };

  [
    'gridProviderEnergy',
    'foreignCompanyIdEnergy',
    'gridProviderGas',
    'foreignCompanyIdGas',
    'craftsChamberArea',
    'craftsChamberNumber',
    'craftsChamberDate',
    'businessRegistrationDate',
    'businessRegistrationLocation',
    'commercialRegisterNumber',
    'commercialRegisterLocation',
    'commercialRegisterDate',
  ].forEach(upsertFormDataEntry);

  const documentEntries = documents.map(prepDocumentForMutation);

  const gasType = prepValueForEmail(
    EIV_ENTRY_TYPE_TO_GERMAN_NAME[installerStatusSummary[BRANCH_TYPE.GAS].entryType]
  );
  const energyType = prepValueForEmail(
    EIV_ENTRY_TYPE_TO_GERMAN_NAME[installerStatusSummary[BRANCH_TYPE.ENERGY].entryType]
  );
  const isEnergyExtended = firstPageValues.energyExtension;

  const isGasExtented = firstPageValues.gasExtension;
  const hasEnergyRow = doesRegisteredInstallerHaveBranch(
    BRANCH_TYPE.ENERGY,
    installerStatusSummary
  );
  const hasGasRow = doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary);

  const isBag = getTenantId() === TENANT_ID.BAG;
  const hasGasEntry = ['main', 'guest'].includes(installerStatusSummary[BRANCH_TYPE.GAS].entryType);
  const liabilityInsuranceEntry =
    initialData.betriebshaftpflicht == null
      ? null
      : {
          first: 'Betriebshaftpflicht',
          second: initialData.betriebshaftpflicht ? '1' : '0',
        };

  const baseDataEntries = [
    { first: 'Email', second: initialData.email },
    { first: 'Eintragungsnummer', second: initialData.companyId },
    liabilityInsuranceEntry,
    conditionalStaticEntry(
      isBag && hasEnergyRow,
      'Beschränkung Strom',
      initialData.limitationEnergy
    ),
    conditionalStaticEntry(isBag && hasGasRow, 'Beschränkung Gas', initialData.limitationGas),
    conditionalStaticEntry(
      hasEnergyRow,
      'Status Strom Eintragung',
      `${energyType} ${isEnergyExtended ? 'verlängert' : ''}`
    ),
    conditionalStaticEntry(
      hasEnergyRow,
      'Strom gültig bis',
      installerStatusSummary[BRANCH_TYPE.ENERGY].expireDate
    ),
    conditionalStaticEntry(
      hasEnergyRow,
      'Betriebsart Strom',
      installerStatusSummary.betriebsartStrom
    ),
    conditionalStaticEntry(
      hasGasEntry,
      'Status Gas Eintragung',
      `${gasType} ${hasGasRow && isGasExtented ? 'verlängert' : ''}`
    ),
    conditionalStaticEntry(
      hasGasRow,
      'Gas gültig bis',
      installerStatusSummary[BRANCH_TYPE.GAS].expireDate
    ),
    conditionalStaticEntry(hasGasRow, 'Betriebsart Gas', installerStatusSummary.betriebsartGas),
    ...getEintragungAlsIdEntries({
      energyId: EINTRAGUNG_ALS_ID.INSTALLER,
      gasId: initialData.bEintragungGasAlsId || EINTRAGUNG_ALS_ID.INSTALLER,
      hasEnergyBranch: hasEnergyRow,
      hasGasBranch: hasGasEntry,
    }),
    { first: 'Anmerkung des Installateurs', second: prepValueForEmail(secondPageValues.changes) },
  ].filter(Boolean);

  if (getTenantId() === TENANT_ID.AVA) {
    baseDataEntries.push({
      first: 'PLZ Auftragsort',
      second: firstPageValues.doesNeedAvaGuestPostal
        ? firstPageValues.avaGuestPostal
        : firstPageValues.companyPostal,
    });
  }

  const sealsEntries = prepDebSeals(initialData, firstPageValues);

  return {
    email: initialData.email,
    subject,
    baseDataEntries,
    formDataEntries,
    expertEntries,
    sealsEntries,
    documents: documentEntries,
    pflegendeBU: installerStatusSummary.pflegendeBu,
    betriebsartStrom: installerStatusSummary.betriebsartStrom,
    betriebsartGas: installerStatusSummary.betriebsartGas,
  };
};

export const prepareDEBInitialData = (installer, experts) => {
  const companyPostal = prepValueForInitialData(installer.bueroPlz);
  const companyCity = prepValueForInitialData(installer.bueroOrt);
  const companyStreet = prepValueForInitialData(installer.bueroStrasse);
  const companyRegion = prepValueForInitialData(installer.bueroOrtsteil);

  const rawHouseNumber = prepValueForInitialData(installer.bueroHausnummer);
  const splitIndex = rawHouseNumber.search(/\D/);
  const companyHouseNumber =
    splitIndex === -1 ? rawHouseNumber : rawHouseNumber.slice(0, splitIndex);
  const companyHouseNumberAddition =
    splitIndex === -1 ? '' : rawHouseNumber.slice(splitIndex).trim();
  const data = {
    companyName1: prepValueForInitialData(installer.firmenname1),
    companyName2: prepValueForInitialData(installer.firmenname2),
    companyName3: prepValueForInitialData(installer.firmenname3),
    companyOwner: prepValueForInitialData(installer.firmeninhaber),
    companyId: `${installer.id}`,
    betriebshaftpflicht: installer.betriebshaftpflicht,
    companyCountry: mapCountryNameToCode(installer.bueroLand) || 'DE',
    companyStreet,
    companyHouseNumber,
    companyPostal,
    companyCity,
    companyRegion,
    companyPostalCity: companyPostal && companyCity ? `${companyPostal}_${companyCity}` : '',
    companyStreetId: companyStreet,
    companyRegionId: companyRegion,
    companyHouseNumberAddition,
    gridProviderEnergy:
      prepValueForInitialData(installer?.bNetzbetreiberStrom?.id) ||
      (installer.eintragungsartStrom === EIV_ENTRY_TYPE.MAIN ? installer.pflegendeBu : ''),
    gridProviderGas:
      prepValueForInitialData(installer?.bNetzbetreiberGas?.id) ||
      (installer.eintragungsartGas === EIV_ENTRY_TYPE.MAIN ? installer.pflegendeBu : ''),
    foreignCompanyIdEnergy: prepValueForInitialData(installer.ausweisnrFremdStrom),
    foreignCompanyIdGas: prepValueForInitialData(installer.ausweisnrFremdGas),
    email: prepValueForInitialData(installer.email),
    phone: prepValueForInitialData(installer.telefonGeschaeft),
    mobile: prepValueForInitialData(installer.telefonMobil),
    isAddressPublic: installer.zuAdressveroeff === 'Ja',
    canSendMail: installer.zuInfoversand === 'Ja',
    limitationGas: prepValueForInitialData(installer.eintragungsartErgGas),
    bEintragungGasAlsId: prepValueForInitialData(installer.bEintragungGasAls?.id),
    limitationEnergy: prepValueForInitialData(installer.eintragungsartErgStrom),
    installerGasValidityDate: prepValueForInitialData(
      parseEIVDateStringToEfixDateString(installer.auswGasGueltig)
    ),
    installerEnergyValidityDate: prepValueForInitialData(
      parseEIVDateStringToEfixDateString(installer.auswStromGueltig)
    ),
    energyAgreement: false,
    gasAgreement: false,
    energyExtension: false,
    gasExtension: false,
    craftsChamberArea: prepValueForInitialData(installer.bHwkbereich?.id),
    craftsChamberNumber: prepValueForInitialData(installer.hwknummer),
    craftsChamberDate: parseEIVDateStringToEfixDateString(installer.hwkdatum),
    businessRegistrationLocation: prepValueForInitialData(installer.gewerbeanmeldungOrt),
    businessRegistrationDate: parseEIVDateStringToEfixDateString(installer.gewerbeanmeldungDatum),
    commercialRegisterNumber: prepValueForInitialData(installer.hrabnummer),
    commercialRegisterLocation: prepValueForInitialData(installer.registergericht),
    commercialRegisterDate: parseEIVDateStringToEfixDateString(installer.registerLetzteAenderung),
    experts: (experts ?? []).map((expert) => ({
      firstName: prepValueForInitialData(expert.vorname),
      lastName: prepValueForInitialData(expert.nachname),
      expertGasValidityDate: parseEIVDateStringToEfixDateString(
        prepValueForInitialData(expert.gueltigBisGas)
      ),
      expertEnergyValidityDate: parseEIVDateStringToEfixDateString(
        prepValueForInitialData(expert.gueltigBisStrom)
      ),
      birthdate: parseEIVDateStringToEfixDateString(expert.geburtsdatum),
      statusGas: expert.statusGas ? expert.statusGas : EIV_STATUS.NOT_SET,
      statusEnergy: expert.statusStrom ? expert.statusStrom : EIV_STATUS.NOT_SET,
      bBerufbezStromId: prepValueForInitialData(expert.bBerufbezStromId),
      bBerufbezGasId: prepValueForInitialData(expert.bBerufbezGasId),
      bFachrichtungStromId: prepValueForInitialData(expert.bFachrichtungStromId),
      bFachrichtungGasId: prepValueForInitialData(expert.bFachrichtungGasId),
      bZusatzStromId: prepValueForInitialData(expert.bZusatzStromId),
      bZusatzGasId: prepValueForInitialData(expert.bZusatzGasId),
      hwkBetriebsleiter: prepValueForInitialData(expert.hwkBetriebsleiter),
      deletionReasonEnergy: '',
      deletionReasonGas: '',
      deletionDateEnergy: '',
      deletionDateGas: '',
      isExpertExtendedEnergy: false,
      isExpertExtendedGas: false,
      salesForceStatus: expert.salesForceStatus,
      telefon: prepValueForInitialData(expert.telefon),
      netzportalEmail: expert.netzportalEmail,
      id: prepValueForInitialData(expert.id),
    })),
  };
  return data;
};

export const prepareDEBCompleteData = (installer, experts, contracts, seals, status) => {
  const finalObject = prepareDEBInitialData(installer, experts);
  finalObject.doesEnergyBranchExist = doesRegisteredInstallerHaveBranch(BRANCH_TYPE.ENERGY, status);
  finalObject.doesGasBranchExist = doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, status);

  const doesNeedAvaGuestPostal =
    getTenantId() === TENANT_ID.AVA && status.installerEntry === INSTALLER_ENTRIES.GUEST_ONLY;

  finalObject.doesNeedAvaGuestPostal = doesNeedAvaGuestPostal;
  finalObject.avaGuestPostal = prepValueForInitialData('');

  const expertsFixed = finalObject.experts.map((expert) => ({
    ...expert,
    completeName: `${expert.firstName} ${expert.lastName}`,
    isMainGas: status[BRANCH_TYPE.GAS].entryType === 'main',
    isMainEnergy: status[BRANCH_TYPE.ENERGY].entryType === 'main',
    isExpertEnergyActive: expert.statusEnergy === EIV_STATUS.ACTIVE,
    isExpertGasActive: expert.statusGas === EIV_STATUS.ACTIVE,
    expertEnergyValidityDate:
      getExpertStatusEnergy(expert) !== '-' ? expert.expertEnergyValidityDate : '-',
    expertGasValidityDate: getExpertStatusGas(expert) !== '-' ? expert.expertGasValidityDate : '-',
    firstQualificationGas: '',
    secondQualificationGas: '',
    firstQualificationEnergy: '',
    secondQualificationEnergy: '',
    isExpertOwner: false,
    hwkBetriebsleiter: expert.hwkBetriebsleiter || '',
  }));

  finalObject.experts = expertsFixed;

  if (shouldFillDebTestData && doesNeedAvaGuestPostal) {
    finalObject.avaGuestPostal = prepValueForInitialData(installer.avaGuestPostal);
  }

  finalObject.contracts = contracts.map((contract) => {
    const contractReturn = {};

    contractReturn.id = prepValueForInitialData(contract.id);
    contractReturn.contractStatus = prepValueForInitialData(contract.vertragsstatus);
    contractReturn.contractType = {
      name: prepValueForInitialData(contract.vertragsart.name),
    };
    contractReturn.validFrom = parseEIVDateStringToEfixDateString(
      prepValueForInitialData(contract.abgeschlossenAm)
    );
    contractReturn.cancelledOn = parseEIVDateStringToEfixDateString(
      prepValueForInitialData(contract.beendetAm)
    );

    return contractReturn;
  });

  finalObject.seals = seals.map((seal) => {
    const sealsReturn = {};

    sealsReturn.id = prepValueForInitialData(seal.id);
    sealsReturn.sealNumber = prepValueForInitialData(seal.zangennr);
    sealsReturn.status = { name: prepValueForInitialData(seal.status?.name) };
    sealsReturn.lostDate = prepValueForInitialData('');
    sealsReturn.lostReason = prepValueForInitialData('');
    sealsReturn.issuedOn = parseEIVDateStringToEfixDateString(
      prepValueForInitialData(seal.ausgegebenAm)
    );
    sealsReturn.returnedOn = parseEIVDateStringToEfixDateString(
      prepValueForInitialData(seal.zurueckgegebenAm)
    );
    sealsReturn.lostOn = parseEIVDateStringToEfixDateString(
      prepValueForInitialData(seal.verlustAm)
    );

    return sealsReturn;
  });

  return finalObject;
};
